<script setup lang="ts">
/**
 * Appendix A/B 공통 — Category / Criteria A–F (클릭 토글 + %).
 * docCode 별로 criteria 상태 분리 (Corporate / Individual 독립).
 */
import { inject, computed } from 'vue';
import { BO_APPENDIX_DOC_CTX } from '../boAppendixContext';
import type { BoAppendixDocCode } from '../boAppendixFields';
import type { BoAppendixPctState } from '../mapBoJsonToAppendix';

const props = defineProps<{ docCode: BoAppendixDocCode; keyPrefix?: string }>();
const ctx = inject(BO_APPENDIX_DOC_CTX)!;
const prefix = computed(() => props.keyPrefix || '');
const cat = computed(() => ctx.criteriaOf(props.docCode).cat);
const crit = computed(() => ctx.criteriaOf(props.docCode).crit);
const pct = computed<BoAppendixPctState>(() => ctx.criteriaOf(props.docCode).pct || {
  direct_shares: '', direct_voting: '',
  indirect_shares: '', indirect_voting: '',
  combined_shares: '', combined_voting: '',
});
function mark(v: boolean) { return v ? '☒' : '☐'; }

const VALUE_KEY: Record<keyof BoAppendixPctState, string> = {
  direct_shares: 'pct_direct_shares',
  direct_voting: 'pct_direct_voting',
  indirect_shares: 'pct_indirect_shares',
  indirect_voting: 'pct_indirect_voting',
  combined_shares: 'pct_combined_shares',
  combined_voting: 'pct_combined_voting',
};

function onPct(key: keyof BoAppendixPctState, e: Event) {
  const el = e.target as HTMLInputElement;
  const v = (el?.value || '').replace(/[^\d.]/g, '');
  ctx.setCriteriaPct(props.docCode, key, v);
  ctx.setFieldValue(`${prefix.value}${VALUE_KEY[key]}`, v);
}
</script>

<template>
  <div class="bo-crit pg-flatten">
    <div class="bo-crit-head">
      <p class="doc-lbl-title">2. Category and criteria of beneficial owner:</p>
      <p class="doc-p cat-lead"><b>Category of Beneficial Owner</b></p>
      <div class="cat-row">
        <span class="doc-toggle" @click="ctx.toggleCat(docCode, 'direct')">{{ mark(cat.direct) }} Direct ownership</span>
        <span class="doc-toggle" @click="ctx.toggleCat(docCode, 'indirect')">{{ mark(cat.indirect) }} Indirect ownership</span>
        <span class="doc-toggle" @click="ctx.toggleCat(docCode, 'both')">{{ mark(cat.both) }} Direct &amp; Indirect ownership</span>
        <span class="doc-toggle" @click="ctx.toggleCat(docCode, 'control')">{{ mark(cat.control) }} Control by other means</span>
      </div>
    </div>

    <table class="cdd-tbl crit-tbl">
      <thead>
        <tr><th class="col-cat">CATEGORY OF BO</th><th>CRITERIA</th></tr>
      </thead>
      <tbody>
        <tr>
          <td class="cdd-lcell cat-cell" rowspan="2">Direct Ownership</td>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'da')">{{ mark(crit.da) }}</span>
            Criteria A — Holds directly in not less than 20% of the shares of the company
            <div class="pct-line">
              [Please state the percentage:
              <input class="pct-in" :class="{ filled: !!pct.direct_shares }"
                :value="pct.direct_shares" @input="onPct('direct_shares', $event)" /> %]
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'db')">{{ mark(crit.db) }}</span>
            Criteria B — Holds directly in not less than 20% of the voting shares of the company
            <div class="pct-line">
              [Please state the percentage:
              <input class="pct-in" :class="{ filled: !!pct.direct_voting }"
                :value="pct.direct_voting" @input="onPct('direct_voting', $event)" /> %]
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl crit-tbl crit-cont">
      <tbody>
        <tr>
          <td class="cdd-lcell cat-cell" rowspan="2">Indirect Ownership</td>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'ia')">{{ mark(crit.ia) }}</span>
            Criteria A — Holds indirectly in not less than 20% of the shares of the company
            <div class="pct-line">
              [Please state the percentage:
              <input class="pct-in" :class="{ filled: !!pct.indirect_shares }"
                :value="pct.indirect_shares" @input="onPct('indirect_shares', $event)" /> %]
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'ib')">{{ mark(crit.ib) }}</span>
            Criteria B — Holds indirectly in not less than 20% of the voting shares of the company
            <div class="pct-line">
              [Please state the percentage:
              <input class="pct-in" :class="{ filled: !!pct.indirect_voting }"
                :value="pct.indirect_voting" @input="onPct('indirect_voting', $event)" /> %]
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl crit-tbl crit-cont">
      <tbody>
        <tr>
          <td class="cdd-lcell cat-cell" rowspan="2">Direct &amp; Indirect Ownership</td>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'ba')">{{ mark(crit.ba) }}</span>
            Criteria A — Holds directly or indirectly in not less than 20% of the shares of the company
            <div class="pct-line">
              [Please state the total percentage (combination of Direct &amp; Indirect must be 20% and more):
              <input class="pct-in" :class="{ filled: !!pct.combined_shares }"
                :value="pct.combined_shares" @input="onPct('combined_shares', $event)" /> %]
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'bb')">{{ mark(crit.bb) }}</span>
            Criteria B — Holds directly or indirectly in not less than 20% of the voting shares of the company
            <div class="pct-line">
              [Please state the total percentage (combination of Direct &amp; Indirect must be 20% and more):
              <input class="pct-in" :class="{ filled: !!pct.combined_voting }"
                :value="pct.combined_voting" @input="onPct('combined_voting', $event)" /> %]
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <table class="cdd-tbl crit-tbl crit-cont">
      <tbody>
        <tr>
          <td class="cdd-lcell cat-cell" rowspan="4">Control by other means</td>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'c')">{{ mark(crit.c) }}</span>
            Criteria C — Has the right to exercise ultimate effective control whether formal or informal over the company or the directors or the management of the company
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'd')">{{ mark(crit.d) }}</span>
            Criteria D — Has the right or power to directly or indirectly appoint or remove a director(s) who holds the majority of the voting rights at the meeting of directors
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'e')">{{ mark(crit.e) }}</span>
            Criteria E — Is a member of the company and, under an agreement with another member of the company, controls alone a majority of the voting rights in the company
          </td>
        </tr>
        <tr>
          <td>
            <span class="doc-toggle" @click="ctx.toggleCrit(docCode, 'f')">{{ mark(crit.f) }}</span>
            Criteria F — Has less than 20% of shares or voting shares but exercises significant control or influence over the company
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p crit-note">
      <b>Note:</b><br />
      - An individual can be a beneficial owner if he meets one or more of the Criteria.<br />
      - You may attach illustration of ownership/control, if any.
    </p>
  </div>
</template>

<style scoped>
.bo-crit { width: 100%; }
.bo-crit-head { margin-bottom: 2px; }
.cat-lead { margin-bottom: 4px !important; }
.cat-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin: 0 0 10px;
}
.cat-row .doc-toggle {
  white-space: nowrap;
  font-size: 12px;
}

.crit-tbl {
  table-layout: fixed;
  width: 100%;
  font-size: 11.5px;
  line-height: 1.4;
}
.crit-tbl th,
.crit-tbl td {
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.crit-tbl .col-cat,
.crit-tbl .cat-cell {
  width: 26%;
  font-weight: 600;
}
.crit-cont {
  margin-top: -1px;
}

.pct-line {
  margin-top: 3px;
  font-size: 11px;
  color: #333;
  line-height: 1.35;
}
.pct-in {
  display: inline-block;
  width: 42px;
  margin: 0 2px;
  padding: 0 4px;
  border: none;
  border-radius: 3px;
  background: #fff6d6;
  box-shadow: inset 0 -2px 0 #f2c94c;
  font: inherit;
  font-weight: 600;
  color: #10305c;
  text-align: center;
  outline: none;
}
.pct-in.filled {
  background: #eaf3ff;
  box-shadow: inset 0 -2px 0 #1B84FF;
}
.crit-note {
  font-size: 11px;
  color: #444;
  padding-top: 8px;
  margin-bottom: 0 !important;
}
</style>
